import React, { useRef } from 'react';
import { Braces } from 'lucide-react';
import HighlightedPrompt from './HighlightedPrompt';
import { PromptVariable } from '../types';
import { extractVariables } from '../utils/variables';

interface PromptTextEditorProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  variables?: PromptVariable[];
  /** Known variable names for the Insert dropdown */
  insertableNames?: string[];
}

/**
 * Textarea editor with Insert Variable control and live ${Variable} highlighting preview.
 */
export const PromptTextEditor: React.FC<PromptTextEditorProps> = ({
  label,
  hint,
  value,
  onChange,
  rows = 6,
  placeholder,
  variables = [],
  insertableNames = [],
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const detected = extractVariables(value);
  const namesForInsert = Array.from(
    new Set([...insertableNames, ...variables.map((v) => v.name), ...detected])
  );

  const insertVariable = (name: string) => {
    const token = `\${${name}}`;
    const el = textareaRef.current;
    if (!el) {
      onChange(value + token);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleInsertClick = () => {
    if (namesForInsert.length === 1) {
      insertVariable(namesForInsert[0]);
      return;
    }
    if (namesForInsert.length > 1) {
      const pick = window.prompt(
        `Insert variable name:\n${namesForInsert.join(', ')}`,
        namesForInsert[0]
      );
      if (pick && /^[A-Za-z_][A-Za-z0-9_]*$/.test(pick.trim())) {
        insertVariable(pick.trim());
      }
      return;
    }
    const pick = window.prompt('Variable name (letters, digits, underscore; not starting with a digit):');
    if (pick && /^[A-Za-z_][A-Za-z0-9_]*$/.test(pick.trim())) {
      insertVariable(pick.trim());
    } else if (pick) {
      window.alert('Invalid variable name. Use ${Name} rules: start with a letter or _, then letters/digits/_.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {hint && <span className="text-gray-500 text-xs ml-1">({hint})</span>}
        </label>
        <button
          type="button"
          onClick={handleInsertClick}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
        >
          <Braces size={14} />
          Insert Variable
        </button>
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
        placeholder={placeholder}
      />
      {detected.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-medium text-gray-500 mb-1">Highlighted preview</p>
          <HighlightedPrompt
            text={value}
            variables={variables}
            className="bg-gray-50 border border-gray-200 p-3 rounded-md text-sm overflow-x-auto max-h-40 overflow-y-auto"
          />
        </div>
      )}
    </div>
  );
};

export default PromptTextEditor;
